import React, { Component } from "react";
import { Dimmer, Grid, Loader } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { CodeGroupDto, UpsertCodeGroupDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { required } from 'utils/validatorUtils';
import { noop } from 'utils/functionUtils';
import {
  addCodeGroup,
  deleteCodeGroup,
  editCodeGroup,
  getAllCodeGroups,
  searchCodeGroups
} from 'services/codeGroupServices';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

interface CodeGroupFieldProps extends FormState {
  codeGroupSearchTerm?: String
}

interface Props extends MessageSourceProps {
}

interface State {
  foundCodeGroups: Array<CodeGroupDto>,
  codeGroupsLoaded: boolean,
  upsertCodeGroup?: UpsertCodeGroupDto,
  upsertActive: boolean,
  loading: boolean,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class CodeGroupsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundCodeGroups: [],
      codeGroupsLoaded: false,
      upsertCodeGroup: undefined,
      upsertActive: false,
      loading: false,
      searchValues: "",
      cancelTokenSource
    };

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  newCodeGroup = () => {

    const defaultValues: CodeGroupDto = {
      id: -1,
      description: "",
      deleted: false
    };

    this.setState(({foundCodeGroups, upsertActive}: State) => ({
      foundCodeGroups: [defaultValues, ...foundCodeGroups],
      upsertCodeGroup: defaultValues,
      upsertActive: !upsertActive
    }))

  };

  editCodeGroup = (id: number) => {

    const {foundCodeGroups, upsertActive} = this.state;
    const codeGroup = foundCodeGroups.find((dos) => {
      return dos.id == id
    });

    this.setState({
      upsertActive: !upsertActive,
      upsertCodeGroup: codeGroup
    });
  };

  searchAfterSubmit = () => {
    const {searchValues, cancelTokenSource} = this.state;
    this.setState({
      codeGroupsLoaded: false
    });

    const onFinally = () => {
      this.setState({
        codeGroupsLoaded: true
      })
    };

    searchCodeGroups(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCodeGroups: response,
            upsertActive: false
          });
        })
        .catch(noop)
        .finally(onFinally);
  };

  handleSubmit = (values: Partial<UpsertCodeGroupDto>) => {

    const {upsertCodeGroup, cancelTokenSource} = this.state;

    let saveValues: Partial<UpsertCodeGroupDto> = {...values};

    const {getMessage} = this.props;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (upsertCodeGroup && upsertCodeGroup.id != -1) {
      editCodeGroup(upsertCodeGroup.id, saveValues, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch(handleError)
    } else {
      addCodeGroup(saveValues, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch(handleError);
    }
  };

  onCancel = () => {
    this.setState(({foundCodeGroups, upsertActive}: State) => ({
      foundCodeGroups: foundCodeGroups.filter(codeGroup => codeGroup.id != -1),
      upsertCodeGroup: undefined,
      upsertActive: !upsertActive
    }));
  };

  deleteCodeGroup = (id: number): void => {
    const {foundCodeGroups, cancelTokenSource} = this.state;

    deleteCodeGroup(id, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCodeGroups: foundCodeGroups.filter(codeGroup => codeGroup.id != response.id)
          })
        })
        .catch(noop);
  };

  handleCodeGroupChange = debounce(({values}: CodeGroupFieldProps): void => {

    if (!this.mounted) {
      return;
    }

    const {codeGroupSearchTerm} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<CodeGroupDto>) => {
      this.setState({
        foundCodeGroups: response
      });
    };

    const onFinally = () => {
      this.setState({
        codeGroupsLoaded: true
      })
    };

    this.setState({
      codeGroupsLoaded: false,
      searchValues: values.codeGroupSearchTerm ? values.codeGroupSearchTerm : ""
    });

    if (codeGroupSearchTerm) {
      searchCodeGroups(codeGroupSearchTerm, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllCodeGroups(false, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {codeGroupsLoaded} = this.state;
    const codeGroupView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchCodeGroupsSegment()}
          </div>

          <StyledViewTableRow>
            {!codeGroupsLoaded ? this.renderLoader() : this.renderCodeGroupsSegment()}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {codeGroupView}
        </React.Fragment>
    );
  }

  renderSearchCodeGroupsSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {upsertActive, foundCodeGroups} = this.state;

    const numResults = foundCodeGroups.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="codeGroupSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} disabled={upsertActive}
                         onClick={() => this.newCodeGroup()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleCodeGroupChange}/>
        </PxGrid>
    )
  };

  descriptionCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {upsertActive, upsertCodeGroup} = this.state;
    const codeGroupId = rowData.id;

    if (upsertCodeGroup && codeGroupId == upsertCodeGroup.id && upsertActive) {
      const validator = (value: string) => required(value);
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={Input}
                validate={validator}
                autoFocus
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData}
        </React.Fragment>
    );
  };

  actionsCellRenderer = ({rowData, cellData, rowIndex}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertCodeGroup} = this.state;
    const codeGroupId = rowData.id;

    if (upsertCodeGroup && codeGroupId == upsertCodeGroup.id && upsertActive) {
      return (
          <React.Fragment>
            <SaveActionButton hoverMessage={getMessage('button.save')}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')} onConfirm={() => this.onCancel()}/>
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.editCodeGroup(cellData)}
              disabled={upsertActive}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("codeGroup.confirmDelete")}
              onConfirm={() => this.deleteCodeGroup(cellData)}
              disabled={upsertActive}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundCodeGroups} = this.state;

    Object.assign(foundCodeGroups[index], {index: index + 1});

    return foundCodeGroups[index];
  };

  foundCodeGroupsRowRenderer = ({className, columns, index, key, style, rowData}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {upsertCodeGroup, upsertActive} = this.state;
    const codeGroupId = rowData.id;

    if (upsertCodeGroup && codeGroupId == upsertCodeGroup.id && upsertActive) {

      const styleCopy = style;

      style = {
        position: "relative"
      };

      return (
          <div style={style}
               key={key}
          >
            <FinalForm
                onSubmit={(values) => this.handleSubmit(values)}
                initialValues={upsertCodeGroup}
                subscription={{values: true, pristine: true}}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit}
                          {...a11yProps}
                          className={className}
                          role="row"
                          style={styleCopy}
                    >

                      {columns}
                    </form>
                )}
            />
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderCodeGroupsSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundCodeGroups} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundCodeGroups.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundCodeGroupsRowRenderer}
            rowHeight={42}
            scrollToIndex={0}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("codeGroup.description")),
                dataKey: "description",
                cellRenderer: (this.descriptionCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("codeGroup.loading")}</Loader>
        </Dimmer>
    )
  };

}

export default withMessages(CodeGroupsView);
